import {
	ContentExpander,
	ErrorLabel,
	Label,
	NumberField,
	Radio,
	Select,
	Spacer
} from '@components';
import { useClearRCSettingsError, useRateCardModifiersState } from '@hooks';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import {
	AllRateCardFuelLevyModifiers,
	AllRateCardSettingFuelLevyModifiers,
	RateCardFormErrors,
	RateCardSettingsTypes,
	RateCardTypes
} from '@state';
import {
	RateCardType,
	TRateCardModifierSet,
	TRateCardModifierType
} from '@types';
import { getRateCardIndex, removeLeadingZeros } from '@utils';
import { useRecoilValue } from 'recoil';
import styles from './Modifiers.module.scss';
import { ModifierBadge } from './ModifierBadge';

type TProps = {
	isSettings?: boolean;
	stopType: 'PICKUP' | 'DROPOFF';
	type: RateCardType;
	isDisabled?: boolean;
	id?: string;
}

export const AdditionalStopsModifierPanel = (props: TProps) => {
	const {
		isSettings,
		stopType,
		type,
		isDisabled = false,
		id
	} = props;
	const [rateCardModifiers, setRateCardModifiers] = useRateCardModifiersState();
	const modifiers = isSettings ? rateCardModifiers.settings : rateCardModifiers.rateCard;
	const rcFormErrors = useRecoilValue(RateCardFormErrors);
	const rateCardSettingsTypes = useRecoilValue(RateCardSettingsTypes);
	const rateCardTypes = useRecoilValue(RateCardTypes);
	const rateCardTypeIndex = getRateCardIndex({
		rateCardSettingsTypes,
		rateCardTypes,
		id,
		isSettings: !!isSettings,
		type
	});
	const errorString = isSettings ? 'rate_card_types' : 'rate_card_prices';
	const clearRCSettingsError = useClearRCSettingsError();
	const stopTypeString = stopType === 'PICKUP' ? 'additional_pickup_stops' : 'additional_drop_off_stops'

	const allRateCardModifiers = useRecoilValue(AllRateCardFuelLevyModifiers);
	const allRateCardSettingModifiers = useRecoilValue(AllRateCardSettingFuelLevyModifiers);
	const fuelLevyModifiers = isSettings ? allRateCardSettingModifiers : allRateCardModifiers;

	const updateAdditionalStopsModifier = (
		key: 'type' | 'value' | 'threshold',
		newValue: string
	) => {
		const update = {
			...modifiers,
			[stopTypeString]:
				newValue === 'None' ? null : {
					...modifiers?.[stopTypeString],
					...(key === 'type' ? {
						value: '',
						threshold: 1
					} : {}),
					[key]: newValue,
					attracts_fuel_levy: !!fuelLevyModifiers?.[stopTypeString]?.attracts_fuel_levy
				}
		} as TRateCardModifierSet;
		const newModifiers = {
			settings: isSettings ? update : undefined,
			rateCard: !isSettings ? update : undefined
		}
		setRateCardModifiers(newModifiers);
		// @ts-ignore
		if (rcFormErrors?.[errorString]?.[rateCardTypeIndex].modifier_set[stopTypeString][key]) {
			clearRCSettingsError(`${errorString}.${rateCardTypeIndex}.modifier_set.${stopTypeString}.${key}`)
		}
	}

	const handleOnBlur = (
		key: 'type' | 'value' | 'threshold',
		newValue: string
	) => {
		if (newValue.startsWith('0') && !newValue.startsWith('0.')) {
			const value = removeLeadingZeros(newValue);
			updateAdditionalStopsModifier(key, value);
		}
		if (newValue.startsWith('.')) {
			updateAdditionalStopsModifier(key, `0${newValue}`)
		}
	}

	const hasError = (key: string) => {
		// @ts-ignore
		return rcFormErrors?.[errorString]?.[rateCardTypeIndex]?.modifier_set?.[stopTypeString]?.[key]?._errors?.[0] || '';
	}

	const getSectionErrors = () => {
		const fieldsWithPossibleError = ['value']
		return fieldsWithPossibleError.map((field) =>
			hasError(field)
		).some((field) => field.length > 0);
	}

	const getConfiguredCount = () => {
		const stops = modifiers?.[stopTypeString];
		if (!stops) return 0;
		return parseFloat(`${stops.value ?? ''}`) > 0 ? 1 : 0;
	}

	if (!modifiers) return

	return (
		<ContentExpander
			title={<div className={styles.expanderTitle}>Additional {stopType === 'PICKUP' ? 'pick-up' : 'drop-off'}
				<ModifierBadge count={getConfiguredCount()} />
				{
					getSectionErrors() ?
						<span className={styles.expanderErrorContainer}>
							<ErrorOutlineOutlinedIcon />
						</span>	: null
				}
			</div>}
			showSpacer
			expanded={false}
			arrowsAsExpanderIcon
			iconColor='var(--neutral-06)'
		>
			<div className={styles.expanderContent}>
				<Label>Charge method</Label>
				<Radio.Group className={styles.radioContainer}>
					<Radio
						name={'charge_method'}
						label={'Flat fee'}
						value={TRateCardModifierType.FIXED}
						onChange={(e) => updateAdditionalStopsModifier('type', e.target.value)}
						checked={modifiers?.[stopTypeString]?.type === TRateCardModifierType.FIXED}
						disabled={isDisabled}
					/>
					<Radio
						name={'charge_method'}
						label={'Per stop'}
						value={TRateCardModifierType.PER_STOP}
						onChange={(e) => updateAdditionalStopsModifier('type', e.target.value)}
						checked={modifiers?.[stopTypeString]?.type === TRateCardModifierType.PER_STOP}
						disabled={isDisabled}
					/>
					<Radio
						name={'charge_method'}
						label={'None'}
						value={'None'}
						onChange={(e) => updateAdditionalStopsModifier('type', e.target.value)}
						checked={modifiers?.[stopTypeString] === null}
						disabled={isDisabled}
					/>
				</Radio.Group>
				{
					modifiers?.[stopTypeString] &&
					<>
						<div className={styles.stopsContainer}>
							<NumberField
								name={stopType === 'PICKUP' ? 'additional_pickup_stops' : 'additional_drop_off_stops'}
								value={`${modifiers?.[stopTypeString]?.value ?? ''}`}
								onChange={(e) => updateAdditionalStopsModifier('value', e.target.value)}
								leftModule={<span className={styles.prefix}>$</span>}
								label={''}
								keepErrorSpace={false}
								hasError={!!hasError('value')}
								disabled={isDisabled}
								min={'0'}
								onBlur={(e) => handleOnBlur('value', e.target.value)}
							/>
							<Label>{modifiers?.[stopTypeString]?.type === TRateCardModifierType.FIXED ? 'after' : 'per stop after'}</Label>
							<Select
								onChange={(val) => updateAdditionalStopsModifier('threshold', val)}
								value={modifiers?.[stopTypeString]?.threshold || 1}
								name={'threshold'}
								disabled={isDisabled}
							>
								{
									[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((val) => {
										return <Select.Option key={val} value={val}>{val}</Select.Option>
									})
								}
							</Select>
							<Label>stop{modifiers?.[stopTypeString]?.threshold > 1 ? 's' : ''}</Label>
						</div>
						{
							!!hasError('value') && <>
								<Spacer size={5} />
								<ErrorLabel errorMessage={hasError('value')} hasError={true} />
							</>
						}
						<Spacer size={20}/>
					</>
				}
			</div>
		</ContentExpander>
	);
}